import React, { useState } from 'react';
import { useResourceWrites, writeErrorMessage } from './useResourceWrites';
import type { AuthoringKind } from './types';

interface DeleteResourceModalProps {
    kind: AuthoringKind;
    id: number;
    /** Singular noun for this kind, e.g. 'help document'. */
    label: string;
    /** What is being deleted, shown so the confirmation names it. */
    name: string;
    onClose: () => void;
    onDeleted: () => Promise<void>;
}

export const DeleteResourceModal: React.FC<DeleteResourceModalProps> = ({
    kind,
    id,
    label,
    name,
    onClose,
    onDeleted
}) => {
    const { remove } = useResourceWrites();
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const confirm = async () => {
        setIsDeleting(true);
        setError(null);
        try {
            await remove(kind, id);
            await onDeleted();
            onClose();
        } catch (err) {
            setError(writeErrorMessage(err, `Could not delete this ${label}. Please try again.`));
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(event) => event.stopPropagation()}
                className="bg-white rounded-lg shadow-xl w-full max-w-[440px] p-6"
            >
                <h2 className="text-xl font-bold text-gray-900 mb-2">Delete this {label}?</h2>
                <p className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-800">{name}</span> will be removed from
                    the resources library for every practice. This cannot be undone.
                </p>

                {error && (
                    <p className="text-sm text-red-500 mt-3" role="alert">
                        {error}
                    </p>
                )}

                <div className="flex justify-end items-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isDeleting}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={confirm}
                        disabled={isDeleting}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDeleting ? 'Deleting…' : `Delete ${label}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeleteResourceModal;
